import { useFadeIn } from '@/hooks/useFadeIn';

interface CaseCardProps {
  title: string;
  desc: string;
  beforeSrc: string;
  afterSrc: string;
  delay: number;
}

function CaseCard({ title, desc, beforeSrc, afterSrc, delay }: CaseCardProps) {
  const fade = useFadeIn({ direction: 'up', delay });
  return (
    <div
      ref={fade.ref}
      style={fade.style}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden"
    >
      <div className="px-5 pt-5 pb-3">
        <h3 className="font-serif text-gray-900 text-base md:text-lg">{title}</h3>
        <p className="font-light text-gray-500 text-sm mt-1">{desc}</p>
      </div>
      <div className="flex gap-2 px-5 pb-5">
        <div className="w-1/2 relative">
          <div className="relative rounded-lg overflow-hidden border border-red-50">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                alt={`${title}(治療前)`}
                className="w-full h-full object-cover object-top"
                loading="lazy"
                src={beforeSrc}
              />
            </div>
            <div className="absolute top-2 left-2 bg-red-500/90 text-white font-medium text-[10px] px-2 py-1 rounded-full whitespace-nowrap">
              BEFORE
            </div>
          </div>
        </div>
        <div className="w-1/2 relative">
          <div className="relative rounded-lg overflow-hidden border border-green-50">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                alt={`${title}(治療後)`}
                className="w-full h-full object-cover object-top"
                loading="lazy"
                src={afterSrc}
              />
            </div>
            <div className="absolute top-2 left-2 bg-green-600/90 text-white font-medium text-[10px] px-2 py-1 rounded-full whitespace-nowrap">
              AFTER
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const cases = [
  {
    title: '先天性ホワイトスポット',
    desc: '生まれつきの白斑も、削らずに自然な色合いへ近づけます。',
    beforeSrc: 'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/38ae7d1d391a4fe31c51218273532600.jpeg',
    afterSrc: 'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/332868c4c1fc8a17b01efb526b461c6b.jpeg',
  },
  {
    title: '矯正後の白斑',
    desc: 'ブラケットを外した後に残った白い点。ICON治療で自然な歯へ。',
    beforeSrc: 'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/d0c6d47c884d83ece0cbb6e3a123d706.png',
    afterSrc: 'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/27357b3e93f8e88dbebb60b4c75319d7.png',
  },
  {
    title: '軽度症例',
    desc: '虫歯初期や脱灰による軽度の白斑。',
    beforeSrc: 'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/82c52c700c0ab6003f388e18da9d9d69.jpeg',
    afterSrc: 'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/b4306c844d8827c6a82a81acdcb7db0a.jpeg',
  },
  {
    title: '中等度症例',
    desc: '範囲が広くやや濃い白斑も、ICON治療で改善の可能性があります。',
    beforeSrc: 'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/7644c17f83e2488f6e9f3e0cfeb63cfe.jpeg',
    afterSrc: 'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/64560b534df1c2cc5ca7f8d95a21a47e.jpeg',
  },
];

export default function IconCases() {
  const heading = useFadeIn({ direction: 'up' });
  const note = useFadeIn({ direction: 'up', delay: 300 });

  return (
    <section className="bg-gray-50 py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div ref={heading.ref} style={heading.style} className="text-center mb-12 md:mb-16">
          <span
            className="inline-block font-medium text-xs md:text-sm tracking-[0.2em]"
            style={{ color: '#3E5F8F' }}
          >
            Cases
          </span>
          <div className="w-8 h-px mt-3 mb-4 mx-auto" style={{ backgroundColor: '#3E5F8F' }}></div>
          <h2 className="font-serif text-gray-900 text-2xl md:text-4xl leading-relaxed">
            その他の症例
          </h2>
          <p className="font-light text-gray-500 text-sm md:text-base mt-4 leading-relaxed max-w-xl mx-auto">
            様々なタイプの白斑に対応しています。<br className="hidden md:block" />
            患者様一人ひとりの状態に合わせた治療をご提案します。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {cases.map((c, i) => (
            <CaseCard
              key={c.title}
              title={c.title}
              desc={c.desc}
              beforeSrc={c.beforeSrc}
              afterSrc={c.afterSrc}
              delay={i * 80}
            />
          ))}
        </div>

        <div ref={note.ref} style={note.style} className="text-center mt-10 md:mt-12">
          <p className="font-light text-gray-500 text-xs md:text-sm leading-relaxed">
            白斑の濃さ・範囲・深さによって改善度は異なります。<br className="hidden md:block" />
            無料カウンセリングでお口の状態を確認し、適切な治療計画をご提案します。
          </p>
        </div>
      </div>
    </section>
  );
}